import React from 'react';
import classNames, { Argument } from 'classnames';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';

import { useSelector } from '../../hooks';
import { isLowPerfMode } from 'modules/app/selectors';

import Icon from './Icon';
import Spinner from './Spinner';
import Button, { ButtonType } from './Button';
import MandatorySymbol from './MandatorySymbol';

const getOptionClassName = ({ selected, active }: { selected: boolean, active: boolean }) => classNames(
  'relative cursor-pointer select-none py-2 px-4 kiosk:py-4 rounded-input',
  {
    'bg-overlay text-default': selected,
    'text-low hover:bg-overlay': !selected,
    'bg-overlay ': active,
  }
);

interface SelectInputProps<T> {
  data: T;
  label: string;
  loading?: boolean;
  multiple?: boolean;
  required?: boolean;
  className?: Argument;
  'data-testid'?: string;
  value: keyof T | Array<keyof T>;
  inputOptionBottom?: boolean;
  onChange: (value: keyof T | Array<keyof T>) => void;
}

const transitions = {
  leaveTo: 'opacity-0',
  leaveFrom: 'opacity-100',
  leave: 'transition ease-in duration-100',
};

const SelectInput = <T extends Record<string, React.ReactNode>>({
  label, value, onChange, data, 'data-testid': dataTestId, multiple, inputOptionBottom, className, loading, required,
}: SelectInputProps<T>) => {
  const lowPerfMode = useSelector(isLowPerfMode);

  const handleRemove = React.useCallback((item: keyof T) => (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onChange((value as Array<keyof T>).filter((i) => i !== item));
  }, [onChange, value]);

  const hasValue = multiple ? (value as Array<keyof T>).length > 0 : Boolean(value);

  return (
    <Listbox value={value} onChange={onChange} multiple={multiple} disabled={loading}>
      <div className="relative">
        <div className="group bg-input-bg rounded-input">
          <span className={classNames('input-label pointer-events-none', {
            'label-no-value peer-focus:translate-y-0 kiosk:peer-focus:translate-y-0': !hasValue,
          })}
          >
            {required && <MandatorySymbol />}
            {label}
          </span>
          <ListboxButton
            className="text-left input peer min-h-[56px] kiosk:min-h-[120px]"
            data-testid={dataTestId}
          >
            <>
              {multiple && (
                <div className="flex flex-row w-full flex-wrap mt-1">
                  {(value as Array<keyof T>).map((item: string) => (
                    <div
                      key={item}
                      className="flex flex-row bg-input-multiple-tag-bg text-input-multiple-tag-label rounded-input px-2 py-1 kiosk:p-4 mr-1 mb-1"
                    >
                      {data[item]}
                      <Button
                        testId="remove-item"
                        type={ButtonType.LIGHT}
                        onClick={handleRemove(item)}
                        className="!p-0 ml-4 kiosk:p-0"
                      >
                        <Icon className="fb-icon--close text-xl kiosk:text-2xl text-default" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              {!multiple && data[value as keyof T]}
              <span
                className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 kiosk:pr-4"
              >
                {loading && <Spinner />}
                {!loading && <Icon className="fb-icon--arrow-down text-2xl kiosk:text-4xl text-lowest" />}
              </span>
            </>
          </ListboxButton>
        </div>
        <Transition {...(!lowPerfMode ? transitions : {})}>
          <ListboxOptions
            as="ul"
            className={classNames(
              // eslint-disable-next-line max-len
              'absolute z-40 !mt-1 right-0 left-0 max-h-1/2-screen kioskSharebox:max-h-96 overflow-auto rounded-md shadow-sm bg-white ring-1 ring-select-border focus:outline-none p-select-spacing',
              { 'bottom-full': inputOptionBottom },
              className,
            )}
          >
            {Object.entries(data).map(([key, option]) => (
              <ListboxOption
                as="li"
                key={key}
                value={key}
                data-testid={`option-${key}`}
                className={getOptionClassName}
              >
                {({ selected }) => (
                  <span className={classNames('flex truncate text-sm kiosk:text-2xl kioskSharebox:text-xl rounded-select', {
                    'font-bold text-brand-high': selected,
                    'font-normal': !selected,
                  })}
                  >
                    {multiple && (
                      <div className="flex items-center pl-2 kiosk:pl-6 pr-2">
                        <input type="checkbox" checked={selected} />
                      </div>
                    )}
                    {option}
                  </span>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </Transition>
      </div>
    </Listbox>
  );
};

export default SelectInput;
